// Оформление: превращение кандидата в сотрудника. Выполняют КАДРЫ, а не рекрутёр — так стоит
// в тз 5.8.1 и в матрице прав (`hiring.candidate.promote` есть у кадров и нет у рекрутёра).
//
// Вкладка отвечает на один вопрос: можно ли оформлять этого человека прямо сейчас, а если
// нет — чего не хватает. Поэтому она устроена как список требований с ответами, а не как
// форма: форма живёт в модалке и открывается, когда требования сошлись.
//
// Три требования сервер проверяет сам (`server/app/hiring/services/candidates.py`, promote):
// заполнены фамилия и имя, нет точного совпадения с чёрным списком, есть основание —
// приложенное заявление либо подтверждение кадров. Мы показываем их ДО нажатия, чтобы человек
// не узнавал об этом отказом на седьмом заполненном поле.

import { RiCheckLine, RiCloseLine, RiUserFollowLine } from '@remixicon/react';
import { Link } from 'react-router';

import { formatRubles } from '@/shared/money/format';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as LinkButton from '@/shared/ui/link-button';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { EmptyState } from '@/shared/ui/screen-state';

import type { CandidateOut } from '../api/candidates';
import { useCan } from '../components/gates';
import { useTicketCompensationQuery } from '../queries';

import { PromoteDialog } from './PromoteDialog';

const PROMOTE_PERMISSION = 'hiring.candidate.promote';

export function HiringTab({ candidate }: { candidate: CandidateOut }) {
  const canPromote = useCan(PROMOTE_PERMISSION);

  if (candidate.employee_id !== null) {
    return <УжеОформлен candidate={candidate} employeeId={candidate.employee_id} />;
  }

  const требования = собратьТребования(candidate);
  const всёСошлось = требования.every((требование) => требование.выполнено);

  return (
    <div className='flex flex-col gap-3 p-5'>
      <FieldList title='Что нужно для оформления'>
        {требования.map((требование) => (
          <FieldRow
            key={требование.название}
            label={требование.название}
            value={
              <span className='flex items-start gap-1.5'>
                {требование.выполнено ? (
                  <RiCheckLine className='mt-0.5 size-4 shrink-0 text-success-base' />
                ) : (
                  <RiCloseLine className='mt-0.5 size-4 shrink-0 text-error-base' />
                )}
                <span>{требование.ответ}</span>
              </span>
            }
            note={требование.шаг}
          />
        ))}
      </FieldList>

      {canPromote ? (
        <PromoteDialog candidate={candidate} готово={всёСошлось} />
      ) : (
        <p className='text-paragraph-sm text-text-sub-600'>
          Перевод в сотрудники выполняют кадры: у них есть отдельное право на это действие, у
          рекрутёра его нет намеренно — оформление заводит карточку в реестре людей и запускает
          табель. Когда кандидат готов, передайте его кадрам; всё, что нужно для решения,
          перечислено выше.
        </p>
      )}
    </div>
  );
}

/** Уже оформлен: что произошло, куда идти дальше и что с билетом. */
function УжеОформлен({ candidate, employeeId }: { candidate: CandidateOut; employeeId: string }) {
  // Компенсация билета применяется РАСЧЁТОМ системы, найм её только показывает: своя
  // реализация уменьшила бы билетный долг дважды. Факта может не быть вовсе — это обычное
  // дело, а не сбой, поэтому запрос без повторов.
  const билет = useTicketCompensationQuery(candidate.id, true);

  return (
    <div className='flex flex-col gap-3 p-5'>
      <FieldList title='Оформлен'>
        <FieldRow
          label='Дата приёма'
          value={candidate.hired_at === null ? null : formatDateOnly(candidate.hired_at)}
          missing='дата не проставлена'
        />
        <FieldRow
          label='Карточка сотрудника'
          value={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={`/people/employees/r/${employeeId}`}>Открыть в разделе «Люди»</Link>
            </LinkButton.Root>
          }
          note='Дальше человек живёт там: тариф, назначение на объект, табель и расчёт.'
        />
      </FieldList>

      <FieldList title='Компенсация билета'>
        {билет.isPending ? (
          <FieldRow label='Компенсация билета' value={null} pending />
        ) : билет.data === undefined ? (
          <FieldRow
            label='Компенсация билета'
            value={null}
            // «Факта нет» и «спросить не удалось» — разные вещи (правило 7). Первое обычное
            // дело: компенсация применяется по отработанной вахте, и до неё её просто не
            // было. Второе означает, что мы не знаем, и написать «не применялась» было бы
            // враньём в денежном вопросе.
            missing={
              билет.error !== null && билет.error.status !== 404
                ? `Спросить не удалось: ${билет.error.envelope.message}. Обновите экран.`
                : 'Пока не применялась. Правило считает система по отработанной вахте: найм показывает факт и сам билетный долг не трогает.'
            }
          />
        ) : (
          <>
            <FieldRow
              label='Сумма'
              // Число стоит в фразе рядом с датой, а не столбцом: знак рубля обязателен.
              value={`${formatRubles(билет.data.amount_kopecks)}`}
            />
            <FieldRow
              label='Когда применена'
              value={parseMoment(билет.data.applied_at)?.toLocaleString('ru-RU') ?? null}
              missing='время не пришло'
              note={билет.data.note}
            />
          </>
        )}
      </FieldList>
    </div>
  );
}

interface Требование {
  название: string;
  ответ: string;
  выполнено: boolean;
  /** Что сделать, если не выполнено. У выполненного шага нет — и это правильно. */
  шаг?: string;
}

/**
 * Требования сервера, показанные до нажатия. Порядок — от того, что решает судьбу человека,
 * к тому, что решает бумага.
 */
function собратьТребования(candidate: CandidateOut): Требование[] {
  const имяЕсть =
    candidate.last_name !== null &&
    candidate.last_name !== '' &&
    candidate.first_name !== null &&
    candidate.first_name !== '';

  const сверка: Требование =
    candidate.blacklist_result === 'exact'
      ? {
          название: 'Чёрный список',
          ответ: 'Точное совпадение: оформление заблокировано',
          выполнено: false,
          шаг: 'Решение по совпадению принимает ответственный за чёрный список, а не система. Если это другой человек — запись надо снять либо поправить данные кандидата.',
        }
      : candidate.blacklist_result === 'possible'
        ? {
            название: 'Чёрный список',
            ответ: 'Возможное совпадение: нужно явное подтверждение',
            выполнено: true,
            шаг: 'Оформление пройдёт, но подтверждение останется в журнале сверок вместе с вашим именем. Посмотрите найденную запись прежде, чем подтверждать.',
          }
        : candidate.blacklist_result === 'clear'
          ? { название: 'Чёрный список', ответ: 'Чисто', выполнено: true }
          : {
              название: 'Чёрный список',
              ответ: 'Сверки ещё не было',
              выполнено: true,
              шаг: 'Сверка пройдёт сама при оформлении. Запустить её заранее можно на вкладке «Обзор» — тогда неприятность выяснится до разговора с человеком, а не после.',
            };

  return [
    сверка,
    {
      название: 'Фамилия и имя',
      ответ: имяЕсть ? 'заполнены' : 'не заполнены',
      выполнено: имяЕсть,
      шаг: имяЕсть
        ? undefined
        : 'В реестр сотрудников человек заводится по фамилии и имени. Заполните их в карточке — площадка отдаёт имя не всегда.',
    },
    {
      название: 'Основание',
      ответ:
        candidate.application_file_id !== null
          ? 'приложено заявление о приёме на работу'
          : 'нужно заявление либо подтверждение кадров',
      выполнено: true,
      шаг:
        candidate.application_file_id !== null
          ? undefined
          : 'По трудовому договору нужно приложенное заявление, по иным типам оформления довольно подтверждения кадров — его вписывают при оформлении.',
    },
  ];
}

/** Пустое состояние вкладки, если карточка почему-то пришла без данных. */
export function HiringTabEmpty() {
  return (
    <div className='flex h-full items-center justify-center p-5'>
      <EmptyState
        icon={RiUserFollowLine}
        title='Оформление пока не о чем'
        description='Карточка кандидата ещё не приехала.'
      />
    </div>
  );
}
